/**
 * App routes (react-router; replaces the old hash router in main.tsx).
 *
 *   /login                      sign in
 *   /                           Hubs entryway (DWA users)
 *   /hubs/:hubId                a Hub page (/hubs/home = Home Hub)
 *   /lupa                       Lupa Hub = the full-page chat
 *   /dashboard/:slug            every dashboard, flat by slug (one URL per item)
 *   /lupa-admin[?tab=…]         Lupa Admin (dwa_admin)
 *   /feedback-review            Feedback Review (dwa_admin)
 *   /knowledgebase-sources      Admin → Knowledgebase sources (dwa_admin)
 *   /morning-howl               Operations → Morning Howl (DWA users)
 *
 * Old `/#/…` links are redirected by <LegacyHashRedirect>. Client-role users are
 * pinned to their own dashboard, with no entryway and no Lupa, as before.
 */
import React, { useEffect, useRef } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { LupaChatProvider } from '../contexts/LupaChatContext';
import { AuthPage } from '../components/AuthPage';
import { LupaWidget } from '../components/LupaWidget';
import { PLCDashboardPage } from '../components/PLCDashboardView';
import App from '../App';
import { EntrywayPage } from '../pages/EntrywayPage';
import { HubPage } from '../pages/HubPage';
import { RagAdminPage } from '../pages/RagAdminPage';
import { FeedbackReviewPage } from '../pages/FeedbackReviewPage';
import { KbSourcesPage } from '../pages/KbSourcesPage';
import { MorningHowlPage } from '../pages/MorningHowlPage';
import { MorningStarDashboardPage } from '../pages/MorningStarDashboardPage';
import { DwaInternalDashboardPage } from '../pages/DwaInternalDashboardPage';
import { DEFAULT_DWA_DASHBOARD_SLUG } from '../pages/dwaInternalCatalogue';
import type { DashboardClientId } from '../pages/dashboardRouting';
import { FullHeightHubLayout, HubLayout } from '../hubs/HubHeader';
import { isDwaUser } from '../hubs/hubs';
import { DWA } from '../theme/dwaTokens';
import { legacyHashToPath } from './legacyHash';
import { clientDashboardId, shouldShowLupaWidget } from './routeRules';

const LoadingScreen: React.FC = () => (
  <div
    style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: DWA.entrywayBackground,
      fontFamily: DWA.font,
    }}
  >
    <div style={{ color: DWA.onDarkMuted, fontSize: '1rem' }}>Loading...</div>
  </div>
);

/** Rewrites old `/#/path` URLs to `/path`, on load and on any later hash change. */
export const LegacyHashRedirect: React.FC = () => {
  const navigate = useNavigate();
  // useNavigate() changes identity on every location change; keep the effect to
  // mount + hashchange only, so a redirect can't re-fire on later navigations.
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  // A passive effect, not a layout effect: the router subscribes to history in
  // its own layout effect, which runs after its children's, so an earlier
  // navigate() would change the URL without re-rendering.
  useEffect(() => {
    const redirect = () => {
      const path = legacyHashToPath(window.location.hash);
      if (!path) return;
      // Drop the hash first so a queued hashchange can't redirect a second time.
      window.history.replaceState(window.history.state, '', window.location.pathname + window.location.search);
      navigateRef.current(path, { replace: true });
    };
    redirect();
    window.addEventListener('hashchange', redirect);
    return () => window.removeEventListener('hashchange', redirect);
  }, []);
  return null;
};

const RequireAuth: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();
  if (isLoading) return <LoadingScreen />;
  if (!isAuthenticated) return <Navigate to="/login" replace state={{ from: location }} />;
  return <>{children}</>;
};

const RequireAdmin: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  if (user?.role !== 'dwa_admin') return <Navigate to="/" replace />;
  return <>{children}</>;
};

const LoginGate: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();
  if (isLoading) return <LoadingScreen />;
  if (isAuthenticated) {
    const from = (location.state as { from?: { pathname?: string; search?: string } } | null)?.from;
    const target = from?.pathname && from.pathname !== '/login' ? `${from.pathname}${from.search ?? ''}` : '/';
    return <Navigate to={target} replace />;
  }
  return <AuthPage />;
};

/** Client-role users: their own dashboard only, whatever URL they open. */
const ClientOnlyRoutes: React.FC = () => {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const displayId = clientDashboardId(user);

  if (!displayId) {
    // Misconfigured client account. Show a placeholder rather than Lupa.
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: DWA.font,
        }}
      >
        <div style={{ maxWidth: 480, padding: '2rem', textAlign: 'center' }}>
          <h2 style={{ marginBottom: '0.75rem' }}>Account not yet configured</h2>
          <p style={{ color: DWA.textSecondary }}>
            Your account doesn't have any clients assigned yet. Please contact your administrator.
          </p>
        </div>
      </div>
    );
  }

  const expected = `/dashboard/${displayId}`;
  if (pathname !== expected) return <Navigate to={expected} replace />;
  return <MorningStarDashboardPage clientId={displayId} />;
};

/** `/dashboard/:slug` for DWA users — same dispatch as the old hash router. */
const DashboardRoute: React.FC = () => {
  const { slug = '' } = useParams();
  if (slug === 'priority-life-care') {
    return (
      <HubLayout>
        <PLCDashboardPage />
      </HubLayout>
    );
  }
  if (slug === 'morningstar-senior-living') {
    return (
      <HubLayout>
        <MorningStarDashboardPage clientId={slug as DashboardClientId} />
      </HubLayout>
    );
  }
  if (slug === 'dwa-internal') {
    return <Navigate to={`/dashboard/${DEFAULT_DWA_DASHBOARD_SLUG}`} replace />;
  }
  if (slug.startsWith('dwa-')) {
    return (
      <HubLayout>
        <DwaInternalDashboardPage dashboardSlug={slug} />
      </HubLayout>
    );
  }
  return <Navigate to="/" replace />;
};

const DwaRoutes: React.FC = () => (
  <Routes>
    <Route path="/" element={<EntrywayPage />} />
    {/* Training Hub folded into Admin (2026-11-03). */}
    <Route path="/hubs/training" element={<Navigate to="/hubs/admin" replace />} />
    <Route path="/hubs/:hubId" element={<HubPage />} />
    <Route
      path="/lupa"
      element={
        <FullHeightHubLayout>
          <App />
        </FullHeightHubLayout>
      }
    />
    <Route path="/dashboard/:slug" element={<DashboardRoute />} />
    <Route
      path="/lupa-admin"
      element={
        <RequireAdmin>
          <HubLayout>
            <RagAdminPage />
          </HubLayout>
        </RequireAdmin>
      }
    />
    {/* Backward-compatible alias for old bookmarks. */}
    <Route path="/petal-admin" element={<Navigate to="/lupa-admin" replace />} />
    <Route
      path="/feedback-review"
      element={
        <RequireAdmin>
          <HubLayout>
            <FeedbackReviewPage />
          </HubLayout>
        </RequireAdmin>
      }
    />
    <Route
      path="/knowledgebase-sources"
      element={
        <RequireAdmin>
          <KbSourcesPage />
        </RequireAdmin>
      }
    />
    <Route path="/morning-howl" element={<MorningHowlPage />} />
    <Route path="*" element={<Navigate to="/" replace />} />
  </Routes>
);

const AuthedRoutes: React.FC = () => {
  const { user } = useAuth();
  if (user?.role === 'client') return <ClientOnlyRoutes />;
  return <DwaRoutes />;
};

/** Lupa widget, mounted once for the whole app; hidden on the Lupa Hub and login. */
export const GlobalLupaWidget: React.FC = () => {
  const { user, isAuthenticated } = useAuth();
  const { pathname } = useLocation();
  if (!isAuthenticated || !shouldShowLupaWidget(pathname, user)) return null;
  return <LupaWidget />;
};

/** Scroll to the top on page change (react-router doesn't by default). */
const ScrollToTop: React.FC = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo?.(0, 0);
  }, [pathname]);
  return null;
};

export const AppRoutes: React.FC = () => {
  const { user } = useAuth();
  return (
    // Keyed on the user so signing out drops the previous user's conversations.
    <LupaChatProvider key={user?.id ?? 'signed-out'} enabled={isDwaUser(user)}>
      <LegacyHashRedirect />
      <ScrollToTop />
      <Routes>
        <Route path="/login" element={<LoginGate />} />
        <Route
          path="*"
          element={
            <RequireAuth>
              <AuthedRoutes />
            </RequireAuth>
          }
        />
      </Routes>
      <GlobalLupaWidget />
    </LupaChatProvider>
  );
};

export default AppRoutes;
